import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Slider.module.css';

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The visible text. Wrapped in the same <label> as the track, so there is
   *  no htmlFor to keep in sync and no way to ship one without a name. */
  label: ReactNode;
  /** What the current position means, in words — shown beside the label. */
  valueLabel?: ReactNode;
  /** Words for the two ends, for a reader deciding which way to drag. */
  minLabel?: ReactNode;
  maxLabel?: ReactNode;
}

/**
 * A native range input with the system's own track and thumb drawn over it.
 *
 * Native for the same reason Checkbox is: the arrow keys, Home and End, the
 * tab order, the drag behaviour and what a screen reader announces all come
 * from the browser, and every one of them is lost the moment this becomes a
 * div someone styled to look like a slider.
 *
 * `aria-valuetext` is worth setting by the caller whenever the number alone
 * would not mean anything out loud — "4 months" rather than "121".
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  { label, valueLabel, minLabel, maxLabel, className, ...rest },
  ref,
) {
  return (
    <label className={cx(styles.field, className)}>
      <span className={styles.header}>
        <span className={styles.label}>{label}</span>
        {valueLabel && <span className={styles.value}>{valueLabel}</span>}
      </span>
      <input ref={ref} type="range" className={styles.input} {...rest} />
      {(minLabel || maxLabel) && (
        <span className={styles.ends} aria-hidden="true">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </span>
      )}
    </label>
  );
});
